import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Container from '../components/layout/Container';
import Loading from '../components/common/Loading';
import { getCompetitions, getCompetitionDetails, getFixtures, getStandings } from '../services/public-api';
import './Home.css';

export function Home() {
  const navigate = useNavigate();
  const [competitions, setCompetitions] = useState([]);
  const [featured, setFeatured] = useState(null); // primary active competition
  const [recentResults, setRecentResults] = useState([]);
  const [upcomingFixtures, setUpcomingFixtures] = useState([]);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const data = await getCompetitions();
      const comps = data.competitions || [];
      setCompetitions(comps);

      // Pick the first active competition as the featured one
      const active = comps.find(c => c.status === 'active') || comps[0];
      if (!active) { setLoading(false); return; }

      setFeatured(active);
      const compId = active.competitionId;
      const divId = active.divisionIds?.[0];

      const [detailsRes, standingsRes] = await Promise.allSettled([
        getCompetitionDetails(compId),
        divId ? getStandings(compId, divId) : Promise.resolve(null),
      ]);

      const details = detailsRes.status === 'fulfilled' ? detailsRes.value : null;
      const firstDivId = details?.competition?.divisions?.[0]?.divisionId || divId;

      // Fetch recent results + upcoming fixtures
      const [recentRes, upcomingRes] = await Promise.allSettled([
        firstDivId ? getFixtures(compId, firstDivId, 'completed') : Promise.resolve({ matches: [] }),
        firstDivId ? getFixtures(compId, firstDivId, 'scheduled') : Promise.resolve({ matches: [] }),
      ]);

      if (recentRes.status === 'fulfilled') {
        const sorted = (recentRes.value?.matches || [])
          .sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate))
          .slice(0, 5);
        setRecentResults(sorted);
      }

      if (upcomingRes.status === 'fulfilled') {
        const now = new Date();
        const sorted = (upcomingRes.value?.matches || [])
          .filter(m => new Date(m.scheduledDate) >= now)
          .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))
          .slice(0, 5);
        setUpcomingFixtures(sorted);
      }

      if (standingsRes.status === 'fulfilled' && standingsRes.value) {
        const rows = standingsRes.value?.standings || standingsRes.value?.items || [];
        setStandings(rows.slice(0, 6));
      }
    } catch (err) {
      console.error('Dashboard load error', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => new Date(iso).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
  });

  if (loading) return <Container><Loading size="large" /></Container>;

  return (
    <div className="home">
      {/* Hero */}
      <div className="home-hero">
        <Container>
          <div className="hero-content">
            <h1>🎱 Chester Pool League</h1>
            <p className="hero-subtitle">Standings, fixtures and results for the Chester area pool league</p>
            {featured && (
              <div className="hero-actions">
                <Link to={`/competitions/${featured.competitionId}/standings`} className="btn btn-primary btn-lg">
                  View Standings
                </Link>
                <Link to={`/competitions/${featured.competitionId}/fixtures`} className="btn btn-outline-white btn-lg">
                  View Fixtures
                </Link>
              </div>
            )}
          </div>
        </Container>
      </div>

      <Container>
        <div className="home-dashboard">

          {/* Recent Results */}
          <section className="dashboard-section">
            <div className="section-header">
              <h2>Recent Results</h2>
              {featured && (
                <Link to={`/competitions/${featured.competitionId}/fixtures`} className="see-all">
                  See all →
                </Link>
              )}
            </div>
            {recentResults.length === 0 ? (
              <p className="empty-text">No recent results yet.</p>
            ) : (
              <div className="results-list">
                {recentResults.map(match => (
                  <Link key={match.matchId} to={`/matches/${match.matchId}`} className="result-card">
                    <div className="result-date">{formatDate(match.scheduledDate)}</div>
                    <div className="result-teams">
                      <span className={match.winnerId === match.team1Id ? 'winner' : ''}>{match.team1Name || 'Team 1'}</span>
                      <span className="result-score">{match.team1Score} – {match.team2Score}</span>
                      <span className={match.winnerId === match.team2Id ? 'winner' : ''}>{match.team2Name || 'Team 2'}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* Upcoming Fixtures */}
          <section className="dashboard-section">
            <div className="section-header">
              <h2>Next Fixtures</h2>
              {featured && (
                <Link to={`/competitions/${featured.competitionId}/fixtures`} className="see-all">
                  See all →
                </Link>
              )}
            </div>
            {upcomingFixtures.length === 0 ? (
              <p className="empty-text">No upcoming fixtures scheduled.</p>
            ) : (
              <div className="fixtures-list">
                {upcomingFixtures.map(match => (
                  <div key={match.matchId} className="fixture-card">
                    <div className="fixture-date">{formatDate(match.scheduledDate)}</div>
                    <div className="fixture-teams">
                      <span>{match.team1Name || 'Team 1'}</span>
                      <span className="fixture-vs">vs</span>
                      <span>{match.team2Name || 'Team 2'}</span>
                    </div>
                    {match.location && <div className="fixture-location">📍 {match.location}</div>}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Mini Standings */}
          {standings.length > 0 && featured && (
            <section className="dashboard-section standings-section-home">
              <div className="section-header">
                <h2>Standings</h2>
                <Link to={`/competitions/${featured.competitionId}/standings`} className="see-all">
                  Full table →
                </Link>
              </div>
              <table className="mini-standings">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Team</th>
                    <th>P</th>
                    <th>W</th>
                    <th>L</th>
                    <th>Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((row, i) => (
                    <tr key={row.teamId || i}>
                      <td>{i + 1}</td>
                      <td><Link to={`/teams/${row.teamId}`}>{row.teamName || row.teamId}</Link></td>
                      <td>{row.played ?? row.matchesPlayed ?? '—'}</td>
                      <td>{row.won ?? row.wins ?? '—'}</td>
                      <td>{row.lost ?? row.losses ?? '—'}</td>
                      <td><strong>{row.points ?? '—'}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* All Competitions */}
          {competitions.length > 1 && (
            <section className="dashboard-section competitions-overview">
              <div className="section-header">
                <h2>All Competitions</h2>
              </div>
              <div className="comp-cards">
                {competitions.map(comp => (
                  <Link key={comp.competitionId} to={`/competitions/${comp.competitionId}`} className="comp-card">
                    <div className="comp-card-name">{comp.name}</div>
                    <div className="comp-card-meta">
                      <span className={`status-dot ${comp.status}`}></span>
                      {comp.status}
                      <span className="comp-type">{comp.competitionType}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

        </div>
      </Container>
    </div>
  );
}
